/**
 * Proxy Service
 * Routes API calls through FastAPI backend to Drupal
 * 
 * Flow: Frontend → FastAPI (/api/v1/drupal/{path}) → Drupal (with shared key auth)
 * 
 * Purpose:
 * - Centralizes all Drupal API calls through the FastAPI proxy
 * - FastAPI handles authentication with DRUPAL_SHARED_KEY
 * - Frontend only needs to authenticate with FastAPI (via Azure AD tokens)
 * 
 * @module proxy.service
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { useAuthStore } from '@store/auth.store';
import { API_TIMEOUT } from '@core/constants/app.constants';
import { refreshAccessToken, isTokenExpiringSoon, isTokenExpired } from '@services/api/interceptors';
import { enqueueSnackbar } from 'notistack';
import { getFastAPIUrl } from '@core/utils/env.utils';

/**
 * Drupal proxy path prefix
 * All Drupal API calls go through: /api/v1/drupal/{path}
 */
const PROXY_PREFIX = '/api/v1/drupal';

/**
 * Proxy Axios Instance
 * Configured for FastAPI backend with proper auth headers
 */
let _proxyInstance: AxiosInstance | null = null;

const createProxyInstance = (): AxiosInstance => {
  const baseUrl = getFastAPIUrl();
  
  // if (import.meta.env.DEV) {
  //   console.log(`🔄 Proxy Service: Using FastAPI at ${baseUrl}`);
  // }
  
  const instance = axios.create({
    baseURL: baseUrl,
    timeout: API_TIMEOUT, // Use centralized timeout configuration
    withCredentials: true, // Include cookies for Azure AD auth
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });
  
  // Request interceptor: Add auth token and proactively refresh if needed
  instance.interceptors.request.use(
    async (config) => {
      // Get token from auth store (same as interceptors.ts)
      let token = useAuthStore.getState()?.accessToken;
      
      // Fallback to localStorage if store doesn't have it
      let fallbackToken = token || localStorage.getItem('accessToken');
      
      // Proactive token refresh: Check if token is expiring soon (within 5 minutes)
      // Skip refresh check for refresh endpoint itself to prevent loops
      const isRefreshEndpoint = config.url?.includes('/auth/refresh');
      const skipTokenRefresh = (config as any).skipTokenRefresh;
      
      if (fallbackToken && !skipTokenRefresh && !isRefreshEndpoint) {
        if (isTokenExpiringSoon(fallbackToken, 5)) {
          if (import.meta.env.DEV) {
            console.log('🔄 Proxy: Token expiring soon, refreshing proactively...');
          }
          
          // Attempt proactive refresh
          const newToken = await refreshAccessToken(false);
          if (newToken) {
            fallbackToken = newToken;
            if (import.meta.env.DEV) {
              console.log('✅ Proxy: Token refreshed proactively');
            }
          } else if (isTokenExpired(fallbackToken)) {
            // Token is already expired and refresh failed
            if (import.meta.env.DEV) {
              console.warn('🔒 Proxy: Token expired and refresh failed');
            }
            // Will be handled by 401 error interceptor
          }
        }
      }
      
      // Set Authorization header with Bearer token
      if (fallbackToken && config.headers) {
        config.headers.Authorization = `Bearer ${fallbackToken}`;
      }
      
      // if (import.meta.env.DEV) {
      //   console.log(`🔄 Proxy Request: ${config.method?.toUpperCase()} ${config.url}`, {
      //     hasToken: !!fallbackToken,
      //     tokenPreview: fallbackToken ? `${fallbackToken.substring(0, 20)}...` : 'none',
      //     authorizationHeader: config.headers?.Authorization ? 'Bearer ***' : 'missing',
      //   });
      // }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  
  // Response interceptor: Handle errors
  instance.interceptors.response.use(
    (response) => {
      // if (import.meta.env.DEV) {
      //   console.log(`✅ Proxy Response: ${response.status} from ${response.config.url}`);
      // }
      return response;
    },
    async (error: AxiosError) => {
      if (import.meta.env.DEV) {
        console.error(`❌ Proxy Error:`, error.response?.status, error.message);
      }
      
      // Handle 401 errors - try to refresh token first
      if (error.response?.status === 401) {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
        
        // Skip refresh if this is already a retry or if it's the refresh endpoint itself
        if (originalRequest?._retry || originalRequest?.url?.includes('/auth/refresh')) {
          // Refresh failed or this is the refresh endpoint - clear auth and redirect
          if (import.meta.env.DEV) {
            console.warn('🔒 Token refresh failed or refresh endpoint error - clearing auth');
          }
          
          useAuthStore.getState()?.clearAuth?.();
          
          // Show notification only if not already on login page
          if (!window.location.pathname.includes('/login')) {
            enqueueSnackbar('Your session token has expired. Please login again.', {
              variant: 'warning',
              autoHideDuration: 4000,
            });
          }
          
          setTimeout(() => {
            window.location.href = '/login';
          }, 500);
          return Promise.reject(error);
        }

        // Check if we have a refresh token
        const refreshToken = useAuthStore.getState()?.refreshToken || localStorage.getItem('refreshToken');
        
        if (!refreshToken) {
          // No refresh token available - clear auth and redirect
          if (import.meta.env.DEV) {
            console.warn('🔒 No refresh token available - clearing auth');
          }
          
          useAuthStore.getState()?.clearAuth?.();
          
          // Show notification only if not already on login page
          if (!window.location.pathname.includes('/login')) {
            enqueueSnackbar('Your session token has expired. Please login again.', {
              variant: 'warning',
              autoHideDuration: 4000,
            });
          }
          
          setTimeout(() => {
            window.location.href = '/login';
          }, 500);
          return Promise.reject(error);
        }

        // Try to refresh the token
        try {
          if (import.meta.env.DEV) {
            console.log('🔄 Proxy: Attempting token refresh...');
          }
          
          // Mark this request as a retry to prevent infinite loops
          originalRequest._retry = true;
          
          // Use the centralized refreshAccessToken function
          const newAccessToken = await refreshAccessToken(true);
          
          if (!newAccessToken) {
            throw new Error('Token refresh failed');
          }

          if (import.meta.env.DEV) {
            console.log('✅ Proxy: Token refreshed successfully, retrying request');
          }
          
          // Update the original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          }
          
          // Reset retry flag for the retry
          originalRequest._retry = false;
          
          // Retry the original request
          return instance(originalRequest);
          
        } catch (refreshError: any) {
          // Refresh failed - clear auth and redirect
          if (import.meta.env.DEV) {
            console.error('❌ Proxy: Token refresh failed:', refreshError.message);
          }
          
          useAuthStore.getState()?.clearAuth?.();
          
          // Show notification only if not already on login page
          if (!window.location.pathname.includes('/login')) {
            enqueueSnackbar('Your session token has expired. Please login again.', {
              variant: 'warning',
              autoHideDuration: 4000,
            });
          }
          
          setTimeout(() => {
            window.location.href = '/login';
          }, 500);
          return Promise.reject(refreshError);
        }
      } else if (error.response?.status === 502) {
        console.error('Cannot connect to Drupal service through proxy');
      } else if (error.response?.status === 504) {
        console.error('Drupal service timeout through proxy');
      }
      
      return Promise.reject(error);
    }
  );
  
  return instance;
};

/**
 * Get the proxy axios instance (lazy initialization)
 */
const getProxyInstance = (): AxiosInstance => {
  if (!_proxyInstance) {
    _proxyInstance = createProxyInstance();
  }
  return _proxyInstance;
};

/**
 * Proxy Service Class
 * Routes Drupal API calls through FastAPI proxy
 */
class ProxyService {
  /**
   * Convert a Drupal API path to proxy path
   * Example: /api/tools/list → /api/v1/drupal/api/tools/list
   */
  private toProxyPath(drupalPath: string): string {
    // Ensure path starts with /
    const normalizedPath = drupalPath.startsWith('/') ? drupalPath : `/${drupalPath}`;
    return `${PROXY_PREFIX}${normalizedPath}`;
  }
  
  /**
   * GET request through proxy
   * @param drupalPath - Drupal API path (e.g., /api/tools/list)
   * @param config - Optional axios config
   */
  async get<T = any>(drupalPath: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const proxyPath = this.toProxyPath(drupalPath);
    return getProxyInstance().get<T>(proxyPath, config);
  }
  
  /**
   * POST request through proxy
   * @param drupalPath - Drupal API path
   * @param data - Request body
   * @param config - Optional axios config
   */
  async post<T = any>(drupalPath: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const proxyPath = this.toProxyPath(drupalPath);
    return getProxyInstance().post<T>(proxyPath, data, config);
  }
  
  /**
   * PUT request through proxy
   * @param drupalPath - Drupal API path
   * @param data - Request body
   * @param config - Optional axios config
   */
  async put<T = any>(drupalPath: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const proxyPath = this.toProxyPath(drupalPath);
    return getProxyInstance().put<T>(proxyPath, data, config);
  }
  
  /**
   * PATCH request through proxy
   * @param drupalPath - Drupal API path
   * @param data - Request body
   * @param config - Optional axios config
   */
  async patch<T = any>(drupalPath: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const proxyPath = this.toProxyPath(drupalPath);
    return getProxyInstance().patch<T>(proxyPath, data, config);
  }
  
  /**
   * DELETE request through proxy
   * @param drupalPath - Drupal API path
   * @param config - Optional axios config
   */
  async delete<T = any>(drupalPath: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const proxyPath = this.toProxyPath(drupalPath);
    return getProxyInstance().delete<T>(proxyPath, config);
  }
  
  /**
   * Generic request through proxy
   * @param config - Axios request config with Drupal path in url
   */
  async request<T = any>(config: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const proxyConfig = {
      ...config,
      url: config.url ? this.toProxyPath(config.url) : undefined,
    };
    return getProxyInstance().request<T>(proxyConfig);
  }
  
  /**
   * Get the FastAPI base URL
   */
  getBaseUrl(): string {
    return getFastAPIUrl();
  }
  
  /**
   * Get the raw axios instance (for advanced use cases)
   */
  getInstance(): AxiosInstance {
    return getProxyInstance();
  }
}

// Export singleton instance
export const proxyService = new ProxyService();
export default proxyService;

// Export helper functions
/** @deprecated Use getFastAPIUrl — kept for existing imports */
export const getFastAPIBaseUrl = getFastAPIUrl;

export { getFastAPIUrl, PROXY_PREFIX };
